/**
 * Booking Calendar Component
 *
 * INVARIANTS (DO NOT VIOLATE):
 * 1. Date Model:
 *    - booking_date (DB) = naive DATE in business wall-time
 *    - Calendar display date must exactly match stored booking_date
 *    - Use local Date construction: new Date(year, month-1, day)
 *    - NEVER use Date.UTC or parse as UTC
 *
 * 2. Query Resilience:
 *    - Calendar must NOT join clients table (use customer_name snapshot)
 *    - Must be resilient to null client_id
 *    - Use format(date, 'yyyy-MM-dd') for DATE filters
 */
import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { DashboardNavbar } from "@/components/dashboard/DashboardNavbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { addDays, format, isSameDay } from "date-fns";
import { Calendar as CalendarIcon, Clock, User, Plus, CalendarDays, QrCode, CalendarOff, Share2 } from "lucide-react";
import { formatTime } from "@/utils/dateFormatting";
import { QrCodePayment } from "@/components/booking-shared/QrCodePayment";
import { AppointmentDrawer } from "@/components/dashboard/AppointmentDrawer";
import { supabase } from "@/integrations/supabase/client";

type Appointment = {
  id: string;
  customerName: string;
  service: string;
  date: Date;
  time: string;
  duration: number;
  status: "confirmed" | "completed" | "cancelled";
  price: number;
  tipAmount: number;
  grossAmount: number;
  paymentMethod: "in_person" | "credit_card";
  paymentStatus: "paid" | "pending";
  qrCodeUrl?: string;
  serviceId?: string;
  providerId?: string;
};

const timeSlots = [
  "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM",
  "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM",
  "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
  "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM",
  "5:00 PM", "5:30 PM"
];

// Helper: Calculate end time based on start time and duration
const calculateEndTime = (startTime: string, durationMinutes: number): string => {
  const [time, period] = startTime.split(' ');
  const [hours, minutes] = time.split(':').map(Number);

  let hour24 = hours;
  if (period === 'PM' && hours !== 12) hour24 += 12;
  if (period === 'AM' && hours === 12) hour24 = 0;

  const totalMinutes = hour24 * 60 + minutes + durationMinutes;
  const endHour24 = Math.floor(totalMinutes / 60) % 24;
  const endMinutes = totalMinutes % 60;

  const endHour12 = endHour24 === 0 ? 12 : endHour24 > 12 ? endHour24 - 12 : endHour24;
  const endPeriod = endHour24 >= 12 ? 'PM' : 'AM';

  return `${endHour12}:${endMinutes.toString().padStart(2, '0')} ${endPeriod}`;
};

// Helper: Format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const BookingCalendarPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [blockoutDates, setBlockoutDates] = useState<Date[]>([]);
  const [blockoutReasons, setBlockoutReasons] = useState<Record<string, string>>({});
  const [newAppointment, setNewAppointment] = useState({
    customerName: "",
    service: "Basic Haircut",
    time: "9:00 AM",
    notes: ""
  });

  // Auto-open the add dialog when navigated with ?newAppointment=true
  // (e.g. from CRM "New Appointment" button with &clientName=John+Doe)
  useEffect(() => {
    if (searchParams.get("newAppointment") === "true") {
      const clientName = searchParams.get("clientName") || "";
      setNewAppointment(prev => ({ ...prev, customerName: clientName }));
      setShowAddDialog(true);
      const cleaned = new URLSearchParams(searchParams);
      cleaned.delete("newAppointment");
      cleaned.delete("clientName");
      setSearchParams(cleaned, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const [selectedAppointment, setSelectedAppointment] = useState<null | {
    id: string;
    service: string;
    price: number;
    qrCodeUrl: string;
  }>(null);
  const [drawerAppointment, setDrawerAppointment] = useState<Appointment | null>(null);
  const [businessId, setBusinessId] = useState<string>("");

  // Load appointments from database
  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get business profile
      const { data: businessProfile, error: businessError } = await supabase
        .from('business_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (businessError) throw businessError;
      setBusinessId(businessProfile.id);

      // Fetch bookings (no client join to prevent PostgREST ambiguity)
      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select(`
          id,
          service_name,
          service_price,
          service_duration,
          service_id,
          provider_id,
          booking_date,
          booking_time,
          status,
          payment_method,
          payment_status,
          customer_name,
          tip_amount,
          gross_amount
        `)
        .eq('business_id', businessProfile.id)
        .in('status', ['confirmed', 'completed'])
        .order('booking_date', { ascending: true });

      if (bookingsError) throw bookingsError;

      // Transform bookings to Appointment format
      const transformedAppointments: Appointment[] = (bookings || []).map((booking: any) => {
        // Parse date as local (not UTC) to prevent timezone shift
        const [y, m, d] = booking.booking_date.split('-').map(Number);
        const localDate = new Date(y, m - 1, d);

        // Tripwire: Sanity check that formatted date matches booking_date (dev-only, non-blocking)
        const formattedDate = format(localDate, 'yyyy-MM-dd');
        if (formattedDate !== booking.booking_date) {
          console.warn('[Calendar Tripwire] Date mismatch detected:', {
            bookingDate: booking.booking_date,
            formattedDate,
            localDate
          });
        }

        return {
          id: booking.id,
          customerName: booking.customer_name || 'Unknown',
          service: booking.service_name,
          date: localDate,
          time: formatTime(booking.booking_time),
          duration: booking.service_duration,
          status: booking.status,
          price: booking.service_price,
          tipAmount: Number(booking.tip_amount || 0),
          grossAmount: Number(booking.gross_amount || booking.service_price),
          paymentMethod: booking.payment_method,
          paymentStatus: booking.payment_status === 'paid' ? 'paid' : 'pending',
          qrCodeUrl: booking.payment_method === 'in_person'
            ? `https://api.stripe.com/v1/payment_links/plink_1NpEQIJwXEcQ7JasFGUvj5Bj/qr_codes/pqrc_1NpEQIJwXEcQ7JasDQ7dnV4y`
            : undefined,
          serviceId: booking.service_id ?? undefined,
          providerId: booking.provider_id ?? undefined,
        };
      });

      setAppointments(transformedAppointments);

      // Load blackout dates from DB
      const { data: blackoutRows } = await supabase
        .from('blackout_dates')
        .select('blackout_date, reason')
        .eq('business_id', businessProfile.id);

      if (blackoutRows) {
        const dates: Date[] = [];
        const reasons: Record<string, string> = {};
        blackoutRows.forEach((row: any) => {
          const [y, m, d] = row.blackout_date.split('-').map(Number);
          dates.push(new Date(y, m - 1, d));
          if (row.reason) reasons[row.blackout_date] = row.reason;
        });
        setBlockoutDates(dates);
        setBlockoutReasons(reasons);
      }
    } catch (error) {
      console.error('Error loading appointments:', error);
      toast.error('Failed to load appointments');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateSelect = (selectedDate: Date | undefined) => {
    setDate(selectedDate);
  };
  
  const getAppointmentsForDate = (date: Date | undefined) => {
    if (!date) return [];
    
    return appointments.filter(appointment => 
      appointment.date.getDate() === date.getDate() &&
      appointment.date.getMonth() === date.getMonth() &&
      appointment.date.getFullYear() === date.getFullYear()
    );
  };
  
  const isBlockoutDate = (date: Date | undefined) => {
    if (!date) return false;
    
    return blockoutDates.some(blockedDate => 
      isSameDay(blockedDate, date)
    );
  };

  const getBlockoutReason = (date: Date | undefined) => {
    if (!date) return "";

    const dateString = format(date, "yyyy-MM-dd");
    return blockoutReasons[dateString] || "";
  };

  // Get all dates that have bookings for calendar indicators
  const getDatesWithBookings = () => {
    const datesMap = new Map<string, Date>();
    appointments.forEach(appointment => {
      const dateKey = format(appointment.date, 'yyyy-MM-dd');
      if (!datesMap.has(dateKey)) {
        datesMap.set(dateKey, appointment.date);
      }
    });
    return Array.from(datesMap.values());
  };
  
  const handleRemoveBlockoutDate = async (dateToRemove: Date) => {
    const dateString = format(dateToRemove, "yyyy-MM-dd");
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: bp } = await supabase
        .from('business_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();
      if (!bp) return;

      const { error } = await supabase
        .from('blackout_dates')
        .delete()
        .eq('business_id', bp.id)
        .eq('blackout_date', dateString);
      if (error) throw error;

      setBlockoutDates(blockoutDates.filter(date => !isSameDay(date, dateToRemove)));
      const updatedReasons = {...blockoutReasons};
      delete updatedReasons[dateString];
      setBlockoutReasons(updatedReasons);
      toast.success("Blockout date removed");
    } catch (error) {
      console.error('Error removing blockout date:', error);
      toast.error("Failed to remove blockout date");
    }
  };
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddAppointment = async () => {
    if (!newAppointment.customerName || !date) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get business profile
      const { data: businessProfile, error: businessError } = await supabase
        .from('business_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (businessError) throw businessError;

      // Create or find client by name
      const nameParts = newAppointment.customerName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Check if client exists
      const { data: existingClients } = await supabase
        .from('clients')
        .select('id')
        .eq('user_id', user.id)
        .eq('first_name', firstName)
        .eq('last_name', lastName)
        .limit(1);

      let clientId: string;

      if (existingClients && existingClients.length > 0) {
        clientId = existingClients[0].id;
      } else {
        // Create new client
        const { data: newClient, error: clientError } = await supabase
          .from('clients')
          .insert({
            user_id: user.id,
            first_name: firstName,
            last_name: lastName,
            status: 'active'
          })
          .select('id')
          .single();

        if (clientError) throw clientError;
        clientId = newClient.id;
      }

      // Calculate duration and price based on service
      const duration = newAppointment.service === "Basic Haircut" ? 30 : 
                       newAppointment.service === "Haircut & Beard Trim" ? 45 : 60;
      const price = newAppointment.service === "Basic Haircut" ? 25 :
                    newAppointment.service === "Haircut & Beard Trim" ? 35 : 50;

      // Create booking in database
      const { data: booking, error: bookingError } = await supabase
        .from('bookings')
        .insert({
          business_id: businessProfile.id,
          client_id: clientId,
          service_name: newAppointment.service,
          service_price: price,
          service_duration: duration,
          booking_date: format(date, 'yyyy-MM-dd'),
          appointment_time: newAppointment.time,
          status: 'confirmed',
          payment_status: 'pending',
          payment_method: 'in_person',
          total_amount: price,
          payment_type: 'cash'
        })
        .select('id')
        .single();

      if (bookingError) throw bookingError;

      // Add to local state
      const appointment: Appointment = {
        id: booking.id,
        customerName: newAppointment.customerName,
        service: newAppointment.service,
        date: date,
        time: newAppointment.time,
        duration: duration,
        status: "confirmed",
        price: price,
        paymentMethod: "in_person",
        qrCodeUrl: "https://api.stripe.com/v1/payment_links/plink_1NpEQIJwXEcQ7JasFGUvj5Bj/qr_codes/pqrc_1NpEQIJwXEcQ7JasDQ7dnV4y"
      };
      
      setAppointments([...appointments, appointment]);
      setShowAddDialog(false);
      setNewAppointment({
        customerName: "",
        service: "Basic Haircut",
        time: "9:00 AM",
        notes: ""
      });
      
      toast.success("Appointment added successfully");
    } catch (error: any) {
      console.error('Error creating appointment:', error);
      toast.error(error.message || 'Failed to create appointment');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleInputChange = (field: string, value: string) => {
    setNewAppointment({
      ...newAppointment,
      [field]: value
    });
  };
  
  const generateQrCode = (appointment: Appointment) => {
    if (appointment.paymentMethod !== 'in_person' || !appointment.qrCodeUrl) return null;
    
    setSelectedAppointment({
      id: appointment.id,
      service: appointment.service,
      price: appointment.price,
      qrCodeUrl: appointment.qrCodeUrl
    });
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardNavbar />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="text-2xl font-semibold">Booking Calendar</h1>
              <p className="text-muted-foreground">
                Manage your appointments and availability
              </p>
            </div>
            <div className="mt-4 md:mt-0 space-x-2">
              <Button
                onClick={() => navigate('/dashboard/blackout-dates')}
                variant="outline"
              >
                <CalendarOff className="mr-2 h-4 w-4" />
                Block Time
              </Button>
              <Button 
                onClick={() => setShowAddDialog(true)}
                className="bg-figgy-500 hover:bg-figgy-600"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Appointment
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="md:col-span-1">
              <CardHeader>
                <CardTitle>Select Date</CardTitle>
              </CardHeader>
              <CardContent>
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={handleDateSelect}
                  className="rounded-md border"
                  modifiers={{
                    blocked: blockoutDates,
                    hasBookings: getDatesWithBookings()
                  }}
                  modifiersStyles={{
                    blocked: {
                      backgroundColor: "#fee2e2",
                      color: "#ef4444",
                      fontWeight: 500,
                    }
                  }}
                  modifiersClassNames={{
                    hasBookings: "relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-figgy-500"
                  }}
                />
              </CardContent>
            </Card>
            
            <Card className="md:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle>
                  <div className="flex flex-col space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {date && isSameDay(date, new Date()) && (
                        <span className="bg-figgy-100 text-figgy-700 text-xs px-2 py-0.5 rounded-full font-medium">
                          Today
                        </span>
                      )}
                      <div className="flex items-center space-x-2">
                        <CalendarIcon className="h-5 w-5" />
                        <span>{date ? format(date, "EEEE, MMMM d, yyyy") : "Select a date"}</span>
                      </div>
                      {isBlockoutDate(date) && (
                        <span className="bg-red-100 text-red-600 text-xs px-2 py-0.5 rounded-full font-medium">
                          Blocked
                        </span>
                      )}
                    </div>
                    {date && !isBlockoutDate(date) && (() => {
                      const dayAppointments = getAppointmentsForDate(date);
                      const expectedEarnings = dayAppointments.reduce((sum, apt) => sum + apt.grossAmount, 0);
                      return dayAppointments.length > 0 ? (
                        <p className="text-sm text-muted-foreground font-normal">
                          {dayAppointments.length} {dayAppointments.length === 1 ? 'appointment' : 'appointments'} · {formatCurrency(expectedEarnings)} expected
                        </p>
                      ) : null;
                    })()}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isBlockoutDate(date) ? (
                  <div className="text-center py-12 border border-dashed rounded-lg bg-red-50">
                    <CalendarOff className="h-12 w-12 mx-auto text-red-500 mb-2" />
                    <h3 className="font-medium text-lg">This day is blocked</h3>
                    {getBlockoutReason(date) && (
                      <p className="text-muted-foreground mb-4">
                        Reason: {getBlockoutReason(date)}
                      </p>
                    )}
                    <Button 
                      variant="outline"
                      onClick={() => {
                        if (date) handleRemoveBlockoutDate(date);
                      }}
                    >
                      Remove Blockout
                    </Button>
                  </div>
                ) : getAppointmentsForDate(date).length > 0 ? (
                  <div className="space-y-4">
                    {getAppointmentsForDate(date).map(appointment => {
                      const endTime = appointment.time ? calculateEndTime(appointment.time, appointment.duration) : null;
                      return (
                        <div
                          key={appointment.id}
                          className="p-4 border rounded-lg flex flex-col md:flex-row justify-between gap-4"
                        >
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-medium">{appointment.customerName}</h3>
                                <p className="text-sm text-muted-foreground">{appointment.service}</p>
                              </div>
                              <div className="text-right">
                                <div className="font-semibold">{formatCurrency(appointment.grossAmount)}</div>
                                <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${
                                  appointment.paymentStatus === 'paid'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {appointment.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}
                                </span>
                              </div>
                            </div>
                            {appointment.time && endTime && (
                              <div className="flex items-center gap-4 mt-2">
                                <div className="flex items-center text-sm text-muted-foreground">
                                  <Clock className="h-4 w-4 mr-1" />
                                  <span>{appointment.time} → {endTime}</span>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 md:ml-auto">
                            {appointment.paymentMethod === 'in_person' && appointment.qrCodeUrl && appointment.paymentStatus === 'pending' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex items-center"
                                onClick={() => generateQrCode(appointment)}
                              >
                                <QrCode className="h-4 w-4 mr-1" />
                                QR Code
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDrawerAppointment(appointment)}
                              disabled={appointment.status !== "confirmed"}
                            >
                              Reschedule
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive"
                              onClick={() => setDrawerAppointment(appointment)}
                              disabled={appointment.status !== "confirmed"}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 border border-dashed rounded-lg">
                    <CalendarDays className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <h3 className="font-medium text-lg">Open for bookings</h3>
                    <p className="text-muted-foreground mb-1">
                      No appointments scheduled yet
                    </p>
                    <p className="text-sm text-muted-foreground mb-6">
                      Open hours: 9:00 AM - 5:30 PM
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2 justify-center">
                      <Button
                        onClick={() => setShowAddDialog(true)}
                        className="bg-figgy-500 hover:bg-figgy-600"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Add Appointment
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(window.location.origin + '/book');
                          toast.success('Booking link copied to clipboard');
                        }}
                      >
                        <Share2 className="mr-2 h-4 w-4" />
                        Share Booking Link
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      <Dialog open={!!selectedAppointment} onOpenChange={() => setSelectedAppointment(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Payment QR Code</DialogTitle>
          </DialogHeader>
          {selectedAppointment && (
            <QrCodePayment
              qrCodeUrl={selectedAppointment.qrCodeUrl}
              amount={selectedAppointment.price}
              serviceName={selectedAppointment.service}
              showCard={false}
              className="pt-4"
            />
          )}
        </DialogContent>
      </Dialog>
      
      <AppointmentDrawer
        appointment={drawerAppointment}
        onClose={() => setDrawerAppointment(null)}
        onActionComplete={loadAppointments}
        businessId={businessId}
      />

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Appointment</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="customerName">Customer Name</Label>
              <div className="flex">
                <div className="bg-muted p-2 rounded-l-md flex items-center">
                  <User className="h-4 w-4 text-muted-foreground" />
                </div>
                <Input 
                  id="customerName"
                  value={newAppointment.customerName}
                  onChange={(e) => handleInputChange("customerName", e.target.value)}
                  placeholder="Enter customer name"
                  className="rounded-l-none"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="service">Service</Label>
              <Select 
                value={newAppointment.service}
                onValueChange={(value) => handleInputChange("service", value)}
              >
                <SelectTrigger id="service">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Basic Haircut">Basic Haircut (30 min)</SelectItem>
                  <SelectItem value="Haircut & Beard Trim">Haircut & Beard Trim (45 min)</SelectItem>
                  <SelectItem value="Premium Haircut & Style">Premium Haircut & Style (60 min)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="time">Time</Label>
              <Select 
                value={newAppointment.time}
                onValueChange={(value) => handleInputChange("time", value)}
              >
                <SelectTrigger id="time">
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map(time => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea 
                id="notes"
                value={newAppointment.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                placeholder="Add any additional notes here"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              onClick={() => setShowAddDialog(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddAppointment}
              className="bg-figgy-500 hover:bg-figgy-600"
            >
              Add Appointment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingCalendarPage;
